import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import { DiPython, DiJavascript1, DiReact } from 'react-icons/di';
import { SiGoogledrive } from "react-icons/si";
import { SiGithub, SiJson, SiAndroid, SiVercel, SiSupabase } from "react-icons/si";
import { FaMicrosoft } from "react-icons/fa";
import { BiData } from "react-icons/bi";
import { sentimentAnalysisMetadata } from './projects/MentionlyticsSentiment';
import { carpoolingAppMetadata } from './projects/CarpoolingApp';
import { spotifyAPIMetadata } from './projects/SpotifyAPIProject';
import { BbdaClusteringMetadata } from './projects/BbdaClustering';
import { BbdaInteractiveDashboardMetadata } from './projects/BbdaInteractiveDashboard';
import { SpotifyPlaylistCreatorMetadata } from './projects/SpotifyPlaylistCreatorProject';
import { BbdaRegressionMetadata } from './projects/BbdaRegression';
import { ScannerAppMetadata } from './projects/ScannerApp';
import  { EventPipelineMetadata } from './projects/EventPipeline';
import { MeetingPlannerMetadata } from './projects/MeetingPlanner';
import { HospioMetadata } from './projects/Hospio';

// Container for projects
const ProjectsContainer = styled.div`
  margin-top: -4.5rem;
  padding: 10.5rem 1.5rem 7rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgb(var(--bg));
  color: rgb(var(--fg) / 0.92);
  min-height: 100vh;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 10% 0%, rgba(69, 82, 109, 0.18), transparent 40%),
      radial-gradient(circle at 90% 10%, rgba(107, 120, 139, 0.1), transparent 35%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 9.5rem 1rem 5.5rem;
  }
`;

const ProjectsIntro = styled.div`
  width: 100%;
  max-width: 920px;
  text-align: center;
  margin-bottom: 1.8rem;
`;

const PageTitle = styled.h1`
  margin: 0 0 0.7rem 0;
  font-size: 2.7rem;
  line-height: 1.08;
  letter-spacing: -0.03em;
  color: rgb(var(--fg) / 0.95);
  font-weight: 800;

  span {
    color: rgb(var(--gray));
  }

  @media (max-width: 768px) {
    font-size: 2.05rem;
  }
`;

const IntroText = styled.p`
  margin: 0 auto;
  max-width: 760px;
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--gray) / 0.7);

  @media (max-width: 768px) {
    font-size: 0.94rem;
    line-height: 1.75;
  }
`;

// Grid for project cards
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 360px));
  justify-content: center;
  gap: 1.5rem;
  width: 100%;
  max-width: 1220px;
  margin-top: 1.2rem;
  position: relative;
  z-index: 1;
  align-content: start;
  align-items: stretch;
  overflow-x: hidden;
  padding-bottom: 2.25rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(260px, 320px));
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    justify-content: center;
    padding-bottom: 1.5rem;
  }
`;

// Styled project card with hover effect
const ProjectCard = styled.div`
  background: rgb(var(--fg) / 0.035);
  border-radius: 18px;
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, background 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  height: 100%;
  flex-direction: column;
  border: 1px solid rgb(var(--fg) / 0.06);
  position: relative;
  backdrop-filter: blur(20px) saturate(1.1);
  -webkit-backdrop-filter: blur(20px) saturate(1.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 32px 64px -16px rgb(0 0 0 / calc(0.6 * var(--shadow-mul)));
    border-color: rgb(var(--fg) / 0.14);
    background: rgb(var(--fg) / 0.055);
  }
`;

// WIP Badge (Image-based)
const WIPBadge = styled.img`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 74px;
  height: auto;
  filter: drop-shadow(0 8px 12px rgb(0 0 0 / calc(0.18 * var(--shadow-mul))));
`;

// Project image styling
const ProjectImage = styled.img`
  width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
  object-fit: cover;
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
  display: block;
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  ${ProjectCard}:hover & {
    filter: brightness(1) saturate(1);
  }
`;

// Project content
const ProjectContent = styled.div`
  padding: 1.25rem 1.25rem 1.35rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  min-height: 0;
`;

// Project title with hover effect
const ProjectTitle = styled.h2`
  font-size: 1.35rem;
  margin-bottom: 0.55rem;
  color: rgb(var(--fg) / 0.92);
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.02em;
  transition: color 0.3s;
  min-height: 3.24rem;

  ${ProjectCard}:hover & {
    color: rgb(var(--gray));
  }
`;

// Project description
const ProjectDescription = styled.p`
  font-size: 0.96rem;
  line-height: 1.75;
  color: rgb(var(--gray) / 0.7);
  margin-bottom: 1rem;
  flex-grow: 1;
`;

// Date published
const DatePublished = styled.p`
  font-size: 0.88rem;
  color: rgb(var(--gray) / 0.45);
  margin-top: auto;
  text-align: right;
  font-style: italic;
`;

// Filter container
const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  width: 100%;
  max-width: 1080px;
  position: relative;
  z-index: 50;
  padding: 1.15rem 1.35rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 22px;
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  backdrop-filter: blur(20px) saturate(1.1);
  -webkit-backdrop-filter: blur(20px) saturate(1.1);

  /* Wide desktop: side-by-side, pill left / icons right */
  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: minmax(220px, 26%) 1fr;
    align-items: center;
    gap: 0;
  }

  /* Laptop / narrow desktop: stacked, centred — looks intentional */
  @media (min-width: 769px) and (max-width: 1023px) {
    flex-direction: column;
    align-items: center;
    gap: 1.25rem;
    padding: 1.25rem 1.35rem;
  }

  @media (max-width: 768px) {
    gap: 1.15rem;
    padding: 0.95rem;
    border-radius: 18px;
  }
`;

// Dropdown button and menu for filtering
const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  z-index: 60;

  /* In the wide-desktop grid column, fill the column width */
  @media (min-width: 1024px) {
    width: 100%;
  }
`;

const DropdownButton = styled.button`
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.9);
  border: 1px solid rgb(var(--fg) / 0.14);
  padding: 0.78rem 1rem;
  border-radius: 999px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 320px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  max-width: 90vw;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgb(var(--fg) / 0.28);
    background: rgb(var(--fg) / 0.09);
  }

  /* Fluid within the grid column — no overflow */
  @media (min-width: 1024px) {
    width: 100%;
    max-width: 300px;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 3.2rem;
  left: 0;
  background: rgb(var(--bg) / 0.92);
  border-radius: 16px;
  box-shadow: 0 24px 56px -8px rgb(0 0 0 / calc(0.6 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);
  padding: 0.5rem;
  z-index: 70;
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  width: 320px;
  max-width: 90vw;
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;
  backdrop-filter: blur(28px) saturate(1.3);
  -webkit-backdrop-filter: blur(28px) saturate(1.3);
`;

const DropdownSearchWrapper = styled.div`
  position: sticky;
  top: -0.5rem;
  background: rgb(var(--bg) / 0.95);
  padding: 0.5rem 0 0.55rem 0;
  margin: -0.5rem -0.5rem 0.5rem -0.5rem;
  z-index: 2;
  border-bottom: 1px solid rgb(var(--fg) / 0.06);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
`;

const DropdownSearch = styled.input`
  width: calc(100% - 1rem);
  margin: 0 0.5rem;
  padding: 0.72rem 0.85rem;
  border-radius: 12px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-sizing: border-box;
  background: rgb(var(--fg) / 0.04);
  color: rgb(var(--fg) / 0.92);
  font-size: 0.9rem;

  &::placeholder {
    color: rgb(var(--gray) / 0.45);
  }

  &:focus {
    outline: none;
    border-color: rgb(var(--fg) / 0.24);
    background: rgb(var(--fg) / 0.06);
  }
`;

const DropdownItem = styled.div`
  padding: 0.65rem 0.9rem;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  color: rgb(var(--gray) / 0.7);
  font-size: 0.9rem;

  &:hover {
    background-color: rgb(var(--fg) / 0.05);
    color: rgb(var(--fg) / 0.92);
  }
`;

const FilterTechnologyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.95rem;
  min-width: 340px;

  /* Wide desktop: icons in the right grid column, divider separates the two sides */
  @media (min-width: 1024px) {
    min-width: 0;
    width: 100%;
    padding-left: 2rem;
    border-left: 1px solid rgb(var(--fg) / 0.08);
    align-items: flex-start;
  }

  /* Laptop / narrow desktop: stacked below pill, centred, no divider */
  @media (min-width: 769px) and (max-width: 1023px) {
    min-width: 0;
    width: 100%;
    align-items: center;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
    align-items: center;
    justify-content: center;
  }
`;

const TechIconsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(9, minmax(0, auto));
  justify-content: center;
  column-gap: 1rem;
  row-gap: 0.9rem;
  padding: 0 0.4rem;

  /* Wide desktop: icons flow left-aligned in the right grid column */
  @media (min-width: 1024px) {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 1.1rem;
    padding: 0;
  }

  /* Laptop / narrow desktop: icons centred under the pill */
  @media (min-width: 769px) and (max-width: 1023px) {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1.1rem;
    padding: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(5, minmax(0, auto));
    column-gap: 0.95rem;
    row-gap: 0.8rem;
  }
`;

const ClearFiltersButton = styled.button`
  background: rgba(239, 107, 107, 0.12);
  color: rgba(255, 180, 180, 0.9);
  border: 1px solid rgba(239, 107, 107, 0.26);
  padding: 0.6rem 1rem;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.25s ease, background 0.25s ease, border-color 0.25s ease;
  margin-top: 0.2rem;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  &:hover {
    transform: translateY(-1px);
    background: rgba(239, 107, 107, 0.18);
    border-color: rgba(239, 107, 107, 0.42);
  }
`;

// Projects component
const Projects = () => {
  const navigate = useNavigate();
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const tagDropdownRef = useRef(null);

  // Collect all project metadata in one array
  const projectsData = [
    sentimentAnalysisMetadata,
    carpoolingAppMetadata,
    spotifyAPIMetadata,
    BbdaClusteringMetadata,
    BbdaInteractiveDashboardMetadata,
    SpotifyPlaylistCreatorMetadata,
    BbdaRegressionMetadata,
    ScannerAppMetadata,
    EventPipelineMetadata,
    MeetingPlannerMetadata,
    HospioMetadata,
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setIsTagDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCardClick = (path) => {
    navigate(path);
  };

  const handleTagSelect = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
    setIsTagDropdownOpen(false);
  };

  const handleLanguageSelect = (language) => {
    setSelectedLanguage((prev) => (prev === language ? '' : language));
  };

  const handleClearFilters = () => {
    setSelectedTags([]);
    setSelectedLanguage('');
  };

  // Filter and sort projects based on selected tags and date
  const filteredProjects = projectsData
    .filter((project) =>
      selectedTags.length === 0
        ? true
        : selectedTags.every((tag) => project.tags.includes(tag))
    )
    .filter((project) =>
      selectedLanguage === ''
        ? true
        : project.languages.includes(selectedLanguage)
    )
    .sort((a, b) => new Date(b.datePublished) - new Date(a.datePublished));

  // Get all unique tags from the filtered projects
  const filteredTagsList = Array.from(
    new Set(filteredProjects.flatMap((project) => project.tags))
  ).sort();

  // Get all unique languages from the filtered projects
  const filteredLanguagesList = Array.from(
    new Set(filteredProjects.flatMap((project) => project.languages))
  );

  const filteredTags = filteredTagsList.filter((tag) =>
    tag.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <ProjectsContainer>
      <ProjectsIntro>
        <PageTitle>Selected <span>Projects</span></PageTitle>
        <IntroText>
          A mix of real projects built around real problems, passion projects, and bootcamp projects across analytics, automation, BI, and application development.
        </IntroText>
      </ProjectsIntro>
      <FilterContainer>
        <DropdownContainer ref={tagDropdownRef}>
          <DropdownButton
            type="button"
            onClick={() => setIsTagDropdownOpen((prev) => !prev)}
          >
            Filter by Tags <FiChevronDown />
          </DropdownButton>
          <DropdownMenu $isOpen={isTagDropdownOpen}>
            <DropdownSearchWrapper>
              <DropdownSearch
                type="text"
                placeholder="Search tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </DropdownSearchWrapper>
            {filteredTags.map((tag) => (
              <DropdownItem key={tag} onClick={() => handleTagSelect(tag)}>
                {tag}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </DropdownContainer>
  
        <FilterTechnologyWrapper>
          <TechIconsContainer>
            {filteredLanguagesList.includes('Python') && (
              <div
                style={{
                  cursor: 'pointer',
                  fontSize: '2rem',
                  color: selectedLanguage === 'Python' ? 'rgb(var(--fg) / 0.92)' : 'rgb(var(--gray) / 0.45)',
                  width: '2.4rem',
                  height: '2.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleLanguageSelect('Python')}
              >
                <DiPython />
              </div>
            )}
            {filteredLanguagesList.includes('JavaScript') && (
              <div
                style={{
                  cursor: 'pointer',
                  fontSize: '2rem',
                  color:
                    selectedLanguage === 'JavaScript' ? 'rgb(var(--fg) / 0.92)' : 'rgb(var(--gray) / 0.45)',
                  width: '2.4rem',
                  height: '2.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleLanguageSelect('JavaScript')}
              >
                <DiJavascript1 />
              </div>
            )}
            {filteredLanguagesList.includes('React') && (
              <div
                style={{
                  cursor: 'pointer',
                  fontSize: '2rem',
                  color: selectedLanguage === 'React' ? 'rgb(var(--fg) / 0.92)' : 'rgb(var(--gray) / 0.45)',
                  width: '2.4rem',
                  height: '2.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleLanguageSelect('React')}
              >
                <DiReact />
              </div>
            )}
            {filteredLanguagesList.includes('PowerBI') && (
              <div
                style={{
                  cursor: 'pointer',
                  fontSize: '2rem',
                  color: selectedLanguage === 'PowerBI' ? 'rgb(var(--fg) / 0.92)' : 'rgb(var(--gray) / 0.45)',
                  transition: 'color 0.2s',
                  width: '2.4rem',
                  height: '2.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleLanguageSelect('PowerBI')}
              >
                <FaMicrosoft />
              </div>
            )}
            {filteredLanguagesList.includes('GitHub') && (
              <div
                style={{
                  cursor: 'pointer',
                  fontSize: '2rem',
                  color: selectedLanguage === 'GitHub' ? 'rgb(var(--fg) / 0.92)' : 'rgb(var(--gray) / 0.45)',
                  width: '2.4rem',
                  height: '2.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleLanguageSelect('GitHub')}
              >
                <SiGithub />
              </div>
            )}
            {filteredLanguagesList.includes('JSON') && (
              <div
                style={{
                  cursor: 'pointer',
                  fontSize: '2rem',
                  color: selectedLanguage === 'JSON' ? 'rgb(var(--fg) / 0.92)' : 'rgb(var(--gray) / 0.45)',
                  width: '2.4rem',
                  height: '2.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleLanguageSelect('JSON')}
              >
                <SiJson />
              </div>
            )}
            {filteredLanguagesList.includes('SQL Server') && (
              <div
                style={{
                  cursor: 'pointer',
                  fontSize: '2rem',
                  color: selectedLanguage === 'SQL Server' ? 'rgb(var(--fg) / 0.92)' : 'rgb(var(--gray) / 0.45)',
                  width: '2.4rem',
                  height: '2.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleLanguageSelect('SQL Server')}
              >
                <BiData />
              </div>
            )}
            {filteredLanguagesList.includes('Android') && (
              <div
                style={{
                  cursor: 'pointer',
                  fontSize: '2rem',
                  color: selectedLanguage === 'Android' ? 'rgb(var(--fg) / 0.92)' : 'rgb(var(--gray) / 0.45)',
                  width: '2.4rem',
                  height: '2.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleLanguageSelect('Android')}
              >
                <SiAndroid />
              </div>
            )}
            {filteredLanguagesList.includes('Apps Script') && (
              <div
                style={{
                  cursor: 'pointer',
                  fontSize: '2rem',
                  color: selectedLanguage === 'Apps Script' ? 'rgb(var(--fg) / 0.92)' : 'rgb(var(--gray) / 0.45)',
                  transition: 'color 0.2s',
                  width: '2.4rem',
                  height: '2.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleLanguageSelect('Apps Script')}
              >
                <SiGoogledrive />
              </div>
            )}
            {filteredLanguagesList.includes('Vercel') && (
              <div
                style={{
                  cursor: 'pointer',
                  fontSize: '2rem',
                  color: selectedLanguage === 'Vercel' ? 'rgb(var(--fg) / 0.92)' : 'rgb(var(--gray) / 0.45)',
                  transition: 'color 0.2s',
                  width: '2.4rem',
                  height: '2.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleLanguageSelect('Vercel')}
              >
                <SiVercel />
              </div>
            )}
            {filteredLanguagesList.includes('Supabase') && (
              <div
                style={{
                  cursor: 'pointer',
                  fontSize: '2rem',
                  color: selectedLanguage === 'Supabase' ? 'rgb(var(--fg) / 0.92)' : 'rgb(var(--gray) / 0.45)',
                  transition: 'color 0.2s',
                  width: '2.4rem',
                  height: '2.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleLanguageSelect('Supabase')}
              >
                <SiSupabase />
              </div>
            )}
          </TechIconsContainer>
        </FilterTechnologyWrapper>
      </FilterContainer>
  
      {(selectedTags.length > 0 || selectedLanguage) && (
        <ClearFiltersButton onClick={handleClearFilters}>
          Clear Filters
        </ClearFiltersButton>
      )}
      <GridContainer>
        {filteredProjects.map((project, index) => (
          <ProjectCard key={index} onClick={() => handleCardClick(project.path)}>
            <ProjectImage src={project.coverImage} alt={project.title} />
            {project.status === 'WIP' && (
              <WIPBadge src="/images/wip.png" alt="WIP Badge" />
            )}
            {project.status === 'Halted' && (
              <WIPBadge src="/images/halted.png" alt="Halted Badge" />
            )}
            <ProjectContent>
              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectDescription>{project.description}</ProjectDescription>
              <DatePublished>
                <strong>Published:</strong> {project.datePublished}
              </DatePublished>
            </ProjectContent>
          </ProjectCard>
        ))}
      </GridContainer>
    </ProjectsContainer>
  );
};

export default Projects;